(() => {
  'use strict';

  const finePointer = () => window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches;
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;
  const FOCUSABLE = 'a[href], button:not([disabled]), summary, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const focusables = (scope) => [...scope.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
  const nextFocusableAfter = (element) => focusables(document.body)
    .find((el) => !element.contains(el) && (element.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));

  function syncDailyWellnessRoutes(root = document) {
    root.querySelectorAll('a[href="/collections/zyma-new-daily-wellness"]').forEach((link) => {
      // The current locale may live at /, /ar/, or another Shopify market prefix.
      link.href = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + 'products/estrezym';
    });
  }

  function initShopNav(nav) {
    if (!nav || nav.dataset.zymaShopReady === 'true') return;
    nav.dataset.zymaShopReady = 'true';
    const trigger = nav.querySelector('[data-zyma-shop-trigger]');
    const tabs = [...nav.querySelectorAll('[data-zyma-shop-tab]')];
    const panels = [...nav.querySelectorAll('[data-zyma-shop-panel]')];
    const builder = nav.querySelector('.zyma-shop-mega__builder');
    let closeTimer = 0;
    let tabTimer = 0;
    let triggerPointer = '';
    let openAtPointerDown = false;

    const isOpen = () => nav.classList.contains('is-open');
    const panelFor = (tab) => panels.find((panel) => panel.dataset.zymaShopPanel === tab?.dataset.zymaShopTab);
    const setPanel = (key) => {
      if (!key) return;
      tabs.forEach((tab) => tab.classList.toggle('is-active', tab.dataset.zymaShopTab === key));
      panels.forEach((panel) => { panel.hidden = panel.dataset.zymaShopPanel !== key; });
    };
    const open = () => {
      window.clearTimeout(closeTimer);
      if (isOpen()) return;
      document.querySelectorAll('[data-zyma-nav-details][open]').forEach((item) => item.removeAttribute('open'));
      nav.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
    };
    const close = (delay = 0) => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        nav.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
      }, delay);
    };

    tabs.forEach((tab, index) => {
      let tabPointer = '';
      let activeAtPointerDown = false;
      // A short hover delay keeps the panel from flipping while the pointer crosses other tabs on its way to the content.
      tab.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !finePointer()) return;
        window.clearTimeout(tabTimer);
        tabTimer = window.setTimeout(() => setPanel(tab.dataset.zymaShopTab), 90);
      });
      tab.addEventListener('pointerleave', () => window.clearTimeout(tabTimer));
      tab.addEventListener('focus', () => setPanel(tab.dataset.zymaShopTab));
      // Touch: the first tap shows the tab's panel, a second tap follows its link.
      tab.addEventListener('pointerdown', (event) => {
        tabPointer = event.pointerType;
        activeAtPointerDown = tab.classList.contains('is-active');
      });
      tab.addEventListener('click', (event) => {
        const touchLike = tabPointer === 'touch' || tabPointer === 'pen';
        tabPointer = '';
        if (!touchLike || activeAtPointerDown) return;
        event.preventDefault();
        setPanel(tab.dataset.zymaShopTab);
      });
      tab.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          tabs[(index + (event.key === 'ArrowDown' ? 1 : tabs.length - 1)) % tabs.length].focus();
          return;
        }
        // Tab moves into this tab's own panel, so every panel's links are reachable from the keyboard.
        if (event.key !== 'Tab' || event.shiftKey) return;
        const first = focusables(panelFor(tab) || document.createElement('div'))[0];
        if (!first) return;
        event.preventDefault();
        first.focus();
      });
    });

    panels.forEach((panel) => {
      panel.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const items = focusables(panel);
        const index = tabs.findIndex((tab) => tab.dataset.zymaShopTab === panel.dataset.zymaShopPanel);
        if (event.shiftKey && event.target === items[0]) {
          event.preventDefault();
          tabs[index]?.focus();
        } else if (!event.shiftKey && event.target === items[items.length - 1]) {
          const target = tabs[index + 1] || builder;
          if (!target) return;
          event.preventDefault();
          target.focus();
        }
      });
    });

    // The open panel comes after the feature card in the DOM, so leave the menu instead of re-entering it.
    builder?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || event.shiftKey) return;
      const after = nextFocusableAfter(nav);
      if (!after) return;
      event.preventDefault();
      after.focus();
    });

    nav.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse' && finePointer()) open(); });
    nav.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse' && finePointer()) close(120); });
    nav.addEventListener('focusin', open);
    // Only close when focus lands somewhere else; a tap on the panel's blank space has no related target.
    nav.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && !nav.contains(event.relatedTarget)) close();
    });
    nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isOpen()) return;
      close();
      trigger?.focus();
    });

    // Tablets (touch at desktop width): the first tap opens the menu, a second tap follows the Shop link.
    trigger?.addEventListener('pointerdown', (event) => {
      triggerPointer = event.pointerType;
      openAtPointerDown = isOpen();
    });
    trigger?.addEventListener('click', (event) => {
      const touchLike = triggerPointer === 'touch' || triggerPointer === 'pen';
      triggerPointer = '';
      if (!touchLike || !desktop() || openAtPointerDown) return;
      event.preventDefault();
      open();
    });

    nav.addEventListener('zyma:shop-close', () => close());
    nav.addEventListener('zyma:shop-open', (event) => {
      open();
      setPanel(event.detail?.key);
    });

    setPanel(tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.zymaShopTab || tabs[0]?.dataset.zymaShopTab);
  }

  const init = (root = document) => {
    syncDailyWellnessRoutes(root);
    root.querySelectorAll('[data-zyma-shop-nav]').forEach(initShopNav);
  };

  document.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Node)) return;
    document.querySelectorAll('[data-zyma-shop-nav].is-open').forEach((nav) => {
      if (!nav.contains(event.target)) nav.dispatchEvent(new CustomEvent('zyma:shop-close'));
    });
  });

  // Theme editor: selecting a "Mega menu · Panel" block opens the menu on that tab.
  const editorTab = (event) => (event.target instanceof Element ? event.target.closest('[data-zyma-shop-tab]') : null);
  document.addEventListener('shopify:block:select', (event) => {
    const tab = editorTab(event);
    tab?.closest('[data-zyma-shop-nav]')?.dispatchEvent(new CustomEvent('zyma:shop-open', { detail: { key: tab.dataset.zymaShopTab } }));
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    editorTab(event)?.closest('[data-zyma-shop-nav]')?.dispatchEvent(new CustomEvent('zyma:shop-close'));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
